import React, { FC, useState } from 'react'
import { CircularProgress } from '@material-ui/core'
import { CellularPayPrompt } from './types'
import { WifiOffIcon } from './icons'
import { useDialogStyles } from './styles'
import { PromptButton, PromptDialog, PromptFooter, PromptHeader } from './dialog-parts'
import { useIsMounted } from './use-is-mounted'

interface GiveUpDialogProps {
  open: boolean
  prompt: CellularPayPrompt
  onKeep: () => void
  onConfirm: () => Promise<void>
}

const TITLE_ID = 'cellular-give-up-title'
const DESCRIPTION_ID = 'cellular-give-up-description'

export const GIVE_UP_FAILURE = 'We could not update your cellular connection. Please try again.'

export const GiveUpDialog: FC<GiveUpDialogProps> = ({ open, onKeep, onConfirm }) => {
  const classes = useDialogStyles()
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const isMounted = useIsMounted()

  const confirm = async () => {
    setSubmitting(true)
    setError('')
    try {
      await onConfirm()
    } catch (e) {
      // onConfirm rejects when the mutation did not give up the connection.
      if (isMounted()) setError(GIVE_UP_FAILURE)
    }
    // On success the prompt takes this dialog off the screen while it refreshes.
    if (isMounted()) setSubmitting(false)
  }

  return (
    <PromptDialog
      blocking
      describedBy={DESCRIPTION_ID}
      labelledBy={TITLE_ID}
      open={open}
    >
      <PromptHeader
        descriptionId={DESCRIPTION_ID}
        icon={<WifiOffIcon size={28} />}
        id={TITLE_ID}
        title='Are you sure you want to give up your connection?'
        tone='destructive'
      >
        Without a network connection, your display can no longer receive updates. You will lose the
        ability to change content, manage playlists, and control your displays remotely. Your screens
        will keep showing whatever content was last loaded.
      </PromptHeader>
      {error && (
        <p
          className={classes.error}
          role='alert'
          style={{ textAlign: 'center' }}
        >
          {error}
        </p>
      )}
      <PromptFooter>
        <PromptButton
          disabled={submitting}
          onClick={onKeep}
          variant='outline'
        >
          Keep my connection
        </PromptButton>
        <PromptButton
          disabled={submitting}
          onClick={confirm}
          variant='primary'
        >
          {submitting && (
            <CircularProgress
              color='inherit'
              size={16}
            />
          )}
          Yes, give up my connection
        </PromptButton>
      </PromptFooter>
    </PromptDialog>
  )
}
